import React, { useEffect, useState } from "react";

import { Dialog } from "primereact/dialog";
import useDashboardStore from "store/dashboard-store";
import { useGetDatasetMapperData } from "api/api-hooks/dataset-mapper-hooks";
import { ProgressSpinner } from "primereact/progressspinner";
import { Sparklines, SparklinesBars, SparklinesLine } from "react-sparklines";
import { T } from "services/lang/LanguageService";
import DatasetLineChart from "../charts/dataset-line-chart/DatasetLineChart";
import SparkLineChart from "../sparkline-advanced/SparkLineChart";
import SparkLineLabel from "./SparkLineLabel";
import { ISparkLineHandlerProps, SPARKLINE_CLICK_SCOPE_CLASS, ValueLabel } from "./SparkLineTypes";
import SparkLineDisconnectedLabel from "./SparkLineDisconnectedLabel";
import "./SparkLine.scss";
import { formatNumber } from "../charts/utils";

const SparkLineHandler = (props: ISparkLineHandlerProps) => {
  const selectedCharts = useDashboardStore(state => state.selectedChartsIdentifiers);
  const setSelectedCharts = useDashboardStore(state => state.setSelectedChartsIdentifiers);

  const [currentDialogDatasets, setCurrentDialogDatasets] = useState<string[]>([]);

  const decimalPlaces = props.decimalPlaces || 0;
  const { data: mapperData } = useGetDatasetMapperData(props.stringValueMapper ?? 0, {
    enabled: !!props.stringValueMapper,
  });

  const showExpandedChart =
    !props.isMultipleCharts ||
    (props.isMultipleCharts &&
      (props.advancedProps?.isExpanded || props.generalProperties?.expandAll));

  const value = props.dataArray[props.dataArray.length - 1];

  const getDecimalPlaces = () => {
    if (!props.advancedProps?.useDecimalsFromHundred) {
      if (value >= 100) {
        return 0
      }
    }
    return decimalPlaces
  }

  const valueContent =
    value != null ? (
      `${formatNumber(value, getDecimalPlaces())} ${props.unit ?? ""}`
    ) : (
      <SparkLineDisconnectedLabel
        isMultipleCharts={props.isMultipleCharts}
        isLoading={props.isLoading}
      />
    );
  const valueTextColor = value != null ? "inherit" : "red";

  const [dialog, setDialog] = useState<boolean>(false);
  const [valueLabel, setValueLabel] = useState<ValueLabel>({
    disconnected: false,
    value,
    decimalPlaces,
    title: props.chartTitle,
    content: valueContent,
    bgColor: "rgba(255, 255, 255, 0.6)",
    textColor: valueTextColor,
  });

  useEffect(() => {
    setValueLabel(prev => ({
      ...prev,
      value,
      decimalPlaces,
      content: valueContent,
      textColor: valueTextColor,
    }));
  }, [value, decimalPlaces, props.unit]);

  let el;
  if ((props.dataArray && props.dataArray.length !== 0) || props.advancedProps?.baseline?.dataset) {
    el = (
      <div className="flex w-full h-full items-center justify-content-center">
        {props.advanced ? (
          <SparkLineChart
            dataset={props.dataset}
            dataArray={props.dataArray}
            advancedProps={props.advancedProps!}
            lineColor={props.lineColor}
            lineWidth={props.lineWidth}
            setValueLabel={setValueLabel}
            chartWidth={props.sparklineWidth}
            chartHeight={props.height}
            minYAxis={props.minYAxis}
            maxYAxis={props.maxYAxis}
            isInlineMultipleCharts={props.advancedProps?.isMultipleChartsInOneBox}
          />
        ) : (
          <div
            style={{
              lineHeight: "2rem",
              width: props.sparklineWidth ? `${props.sparklineWidth}px` : "100%",
              height: props.height ? `${props.height}px` : "auto",
              display: "inline-block",
            }}
          >
            <Sparklines data={props.dataArray} height={30} preserveAspectRatio="none">
              <SparklinesLine
                style={{
                  stroke: props.lineColor ?? "#41c3f9",
                  fill: "none",
                  strokeWidth: props.lineWidth ?? 1,
                }}
              />
              <SparklinesBars style={{ fill: props.lineColor ?? "#41c3f9", fillOpacity: ".25" }} />
            </Sparklines>
          </div>
        )}
      </div>
    );
  } else {
    el = props.isLoading ? null : <h4>{T("word.no-data")}</h4>;
  }

  const identifiers = [
    props.dataset,
    props.advancedProps?.baseline?.usage === "dataset" && props.advancedProps?.baseline?.dataset,
    props.advancedProps?.maxLine?.usage === "dataset" && props.advancedProps?.maxLine?.dataset,
    props.advancedProps?.minLine?.usage === "dataset" && props.advancedProps?.minLine?.dataset,
  ].filter(Boolean) as string[];

  const getClassName = () => {
    const baseClassName = SPARKLINE_CLICK_SCOPE_CLASS
    if (props.isMultipleCharts) {
      return `${baseClassName} ${props.advancedProps?.isExpanded || props.generalProperties?.expandAll ? "sparkline-expanded" : "sparkline-collapsed cursor-pointer"} flex flex-column-reverse ${props.advancedProps?.isMultipleChartsInOneBox ? "h-full" : ""}`;
    }
    return `${baseClassName} flex flex-column h-full`;
  };

  const getHeight = () => {
    if (!props.isMultipleCharts) {
      return `${props.height ? `${props.height}px` : "auto"}`;
    }

    if (props.advancedProps?.isMultipleChartsInOneBox) {
      return `${props.height ? `${props.height}px` : "161px"}`;
    }

    return "100%";
  };

  const handleDoubleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.ctrlKey) {
      if (selectedCharts.length > 0) {
        setCurrentDialogDatasets(selectedCharts);
      } else {
        setCurrentDialogDatasets(identifiers);
      }
    } else {
      setCurrentDialogDatasets(identifiers);
    }
    setDialog(true);
  };

  const chartElement = props.isLoading ? (
    <div className="inline-multiple-charts-spinner">
      <ProgressSpinner className="h-2rem w-2rem" />
    </div>
  ) : (
    <div
      className={
        props.advancedProps?.isMultipleChartsInOneBox
          ? "inline-multiple-charts-sparkline"
          : "flex flex-wrap justify-content-evenly w-full relative"
      }
      style={!props.advancedProps?.isMultipleChartsInOneBox ? { height: getHeight() } : undefined}
      onDoubleClick={handleDoubleClick}
    >
      {el}
    </div>
  );

  const sparkLineLabel = (
    <SparkLineLabel
      valueLabel={valueLabel}
      setValueLabel={setValueLabel}
      isMultipleCharts={props.isMultipleCharts}
      generalProperties={props.generalProperties}
      isExpanded={props.advancedProps?.isExpanded ?? false}
      {...props}
      mappedValue={
        props.stringValueMapper && mapperData
          ? mapperData.mappings[valueLabel.value as number]
          : undefined
      }
      allCharts={props.advancedProps?.allCharts}
    />
  );

  const changeExpandedChartIndex = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.ctrlKey) {
      const newSelectedCharts = [...selectedCharts];
      if (newSelectedCharts.includes(props.dataset)) {
        newSelectedCharts.splice(newSelectedCharts.indexOf(props.dataset), 1);
      } else {
        newSelectedCharts.push(props.dataset);
      }
      setSelectedCharts(newSelectedCharts);
    }
    return (
      !props.advancedProps?.isMultipleChartsInOneBox &&
      props.advancedProps?.setExpandedChartIndex?.(props.chartIndex ?? 0)
    );
  };

  const contentToRender = (
    <>
      <div
        className={getClassName()}
        onClick={changeExpandedChartIndex}
        style={{
          borderRadius: "5px",
          border: selectedCharts.includes(props.dataset) ? "1px solid #007df2" : "",
        }}
      >
        <div className="sparkline-chart-container" onClick={() => setSelectedCharts([])} onDoubleClick={handleDoubleClick}>
          {props.advancedProps?.isMultipleChartsInOneBox || showExpandedChart ? chartElement : null}
        </div>
        <div className="sparkline-label-container">{sparkLineLabel}</div>
      </div>
      <Dialog
        header={T("label.sparkline-datasets-chart")}
        visible={dialog}
        className="sparkline-datasets-chart"
        modal={false}
        onHide={() => setDialog(false)}
      >
        <DatasetLineChart
          title=""
          identifiers={currentDialogDatasets}
          from={props.advancedProps?.fromDate?.toString()}
          to={props.advancedProps?.toDate?.toString()}
          showDateSelection
          dynamicHeight
          chartDialogOpen={dialog}
        />
      </Dialog>
    </>
  );

  return contentToRender;
};

export default SparkLineHandler;
